import React from "react";
import { useT } from "./i18n/context";

type Testimonial = {
  handle: string;
  quote: string;
  source: "HN" | "Reddit" | "X";
};

// Real comments only. Sourced from the Hacker News "Show HN: Cate v1.0" thread
// (https://news.ycombinator.com/item?id=48265470), pulled via the HN API and
// lightly trimmed for length without changing meaning. Add Reddit/X entries
// here as they are collected — keep every entry verifiable to a real comment.
const ROW_A: Testimonial[] = [
  { handle: "aleda145", source: "HN", quote: "Really nice! I tried out a similar thing but with tldraw as the canvas engine. Cool stuff." },
  { handle: "JrProgrammer", source: "HN", quote: "Cool project! I've been playing around with a couple of different implementations of an infinite canvas." },
  { handle: "cl3misch", source: "HN", quote: "A canvas editor is a great idea. Very glad there is development in this space." },
  { handle: "kidfiji", source: "HN", quote: "This single-pane-of-glass approach works better for some folks' mental models." },
];

const ROW_B: Testimonial[] = [
  { handle: "NitpickLawyer", source: "HN", quote: "I really like the amount of exploration going on in this space. The kind we see now is a bit more extreme, and I like it." },
  { handle: "throwthrowuknow", source: "HN", quote: "Window layout isn't the key feature. Creating a spatial map of open files and other resources is." },
  { handle: "jrgd", source: "HN", quote: "I would love to have something like this." },
  { handle: "verve_rat", source: "HN", quote: "I'm actually excited to hope for a cambrian explosion of IDE experiments." },
];

// Deterministic monogram avatar — no fake profile photos. Hue derived from the
// handle so each commenter keeps a stable colour.
function avatarColor(handle: string): string {
  let hash = 0;
  for (let i = 0; i < handle.length; i++) hash = (hash * 31 + handle.charCodeAt(i)) >>> 0;
  return `hsl(${hash % 360}, 45%, 55%)`;
}

function Avatar({ handle }: { handle: string }) {
  return (
    <span
      aria-hidden
      className="flex items-center justify-center rounded-full shrink-0 text-xs font-semibold"
      style={{ width: 28, height: 28, backgroundColor: avatarColor(handle), color: "#fff" }}
    >
      {handle.charAt(0).toUpperCase()}
    </span>
  );
}

function Card({ t }: { t: Testimonial }) {
  return (
    <figure
      className="rounded-lg px-5 py-4 mr-4 flex flex-col gap-2.5 shrink-0"
      style={{
        width: 320,
        backgroundColor: "var(--surface-3)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      <figcaption className="flex items-center gap-2.5">
        <Avatar handle={t.handle} />
        <span className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>
          @{t.handle}
        </span>
        <span className="text-[11px] ml-auto" style={{ color: "var(--text-muted)" }}>
          {t.source}
        </span>
      </figcaption>
      <blockquote className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
        {t.quote}
      </blockquote>
    </figure>
  );
}

function Row({ items, direction }: { items: Testimonial[]; direction: "left" | "right" }) {
  // The track is two identical halves; animating by -50% shifts exactly one
  // half, so the seam is invisible. For that to stay gap-free across the whole
  // viewport, one half must be at least as wide as the screen. With only a few
  // cards per row we repeat the base set so a half comfortably exceeds even
  // ultrawide displays (each card ~336px; 3x4 = 12 cards ≈ 4000px per half).
  const half = [...items, ...items, ...items];
  const loop = [...half, ...half];
  return (
    <div className="marquee-row overflow-hidden marquee-mask">
      <div className={`marquee-track marquee-track--${direction}`}>
        {loop.map((t, i) => (
          <Card key={`${t.handle}-${i}`} t={t} />
        ))}
      </div>
    </div>
  );
}

export function Testimonials() {
  const { home } = useT();
  return (
    <section className="py-24 px-0 overflow-hidden">
      <div className="max-w-3xl mx-auto text-center px-6 mb-12">
        <h2
          className="text-3xl sm:text-4xl font-semibold tracking-tight mb-3"
          style={{ color: "var(--text-primary)" }}
        >
          {home.testimonials.title}
        </h2>
        <p className="text-sm sm:text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          {home.testimonials.subtitle}
        </p>
      </div>
      <div className="flex flex-col gap-4">
        <Row items={ROW_A} direction="left" />
        <Row items={ROW_B} direction="right" />
      </div>
    </section>
  );
}

export default Testimonials;
